import { createLogger } from "../helper/logger.js";
import { PD_THEME_CHANGED_EVENT, THEME_ATTRIBUTE } from "./theme-model.js";
import { parseThemeMirror, sanitizeOverrides } from "./theme-mirror.js";
//#region src/theme/theme-engine.ts
/**
* Theme engine — sets the `theme` attribute on `<html>` (layer 1, CSS
* blocks `[theme="<id>"]`) and editor overrides as inline custom properties
* (layer 2), keeps one localStorage mirror `{ id, overrides? }` for a
* flash-free restart, updates `<meta name="theme-color">` and notifies via
* `PD_THEME_CHANGED_EVENT` on `document`.
*
* Boot order: `initTheme()` is synchronous and belongs to the static init of
* the app, BEFORE `startInit()` — it must not touch the store. The app's
* `index.html` may apply the same mirror in an inline script before the
* bundle (splash in theme colours); `initTheme()` is idempotent to that.
*
* @example
* ```typescript
* initTheme({ storageKey: "app.theme", fallback: "blue", themeColorToken: "--pd-default-darker-col" });
* setTheme("wood");
* setTheme("wood", { persist: false });   // Vorschau: anwenden, Spiegel unverändert
* setThemeOverrides({ "--pd-default-col": "#7a3b12" });
* clearThemeOverrides();
* ```
*/
var logger = createLogger("ThemeEngine");
var config;
var active;
var requireConfig = () => {
	if (!config) throw new Error("pd-spa-helper: initTheme() has not run — call it in the static init, before startInit().");
	return config;
};
var readMirror = (key) => {
	try {
		return parseThemeMirror(localStorage.getItem(key));
	} catch (error) {
		logger.warn("Theme mirror not readable (storage blocked?)", error);
		return;
	}
};
var writeMirror = (key, theme) => {
	try {
		localStorage.setItem(key, JSON.stringify(theme));
	} catch (error) {
		logger.warn("Theme mirror not persisted (storage blocked?)", error);
	}
};
/** Replace the inline overrides on `<html>` — previous ones are removed first. */
var applyOverrides = (previous, next) => {
	const style = document.documentElement.style;
	for (const name of Object.keys(previous ?? {})) style.removeProperty(name);
	for (const [name, value] of Object.entries(next ?? {})) style.setProperty(name, value);
};
/**
* Computed colour of a token via a probe element — resolves `var()` chains
* and relative-colour expressions, which `getPropertyValue` would not.
*/
var resolveColor = (token) => {
	const probe = document.createElement("span");
	probe.style.color = `var(${token})`;
	(document.body ?? document.documentElement).append(probe);
	const color = getComputedStyle(probe).color;
	probe.remove();
	return color;
};
var updateMetaThemeColor = (token) => {
	if (!token) return;
	const color = resolveColor(token);
	if (!color) return;
	let meta = document.querySelector("meta[name=\"theme-color\"]");
	if (!meta) {
		meta = document.createElement("meta");
		meta.name = "theme-color";
		document.head.append(meta);
	}
	meta.content = color;
};
var apply = (next, persist = true) => {
	const cfg = requireConfig();
	document.documentElement.setAttribute(THEME_ATTRIBUTE, next.id);
	applyOverrides(active?.overrides, next.overrides);
	active = next;
	if (persist) writeMirror(cfg.storageKey, next);
	updateMetaThemeColor(cfg.themeColorToken);
	document.dispatchEvent(new CustomEvent(PD_THEME_CHANGED_EVENT, { detail: next }));
	logger.debug(`Theme applied: ${next.id}`);
};
/** Restore the stored theme synchronously — or the fallback — and apply it. */
function initTheme(cfg) {
	config = cfg;
	apply(readMirror(cfg.storageKey) ?? { id: cfg.fallback });
	return active;
}
/**
* Switch the base theme; editor overrides stay in place.
* `persist: false` applies the theme for a preview only — the mirror keeps
* the last persisted theme, so a reload returns to it (settings page: the
* tile click previews, "apply" persists, "cancel" previews the old one).
*/
function setTheme(id, options = {}) {
	const overrides = active?.overrides;
	apply(overrides ? {
		id,
		overrides
	} : { id }, options.persist ?? true);
}
/** Replace the editor overrides (inline custom properties on `<html>`). */
function setThemeOverrides(tokens) {
	const id = active?.id ?? requireConfig().fallback;
	const overrides = sanitizeOverrides(tokens);
	apply(overrides ? {
		id,
		overrides
	} : { id });
}
function clearThemeOverrides() {
	apply({ id: active?.id ?? requireConfig().fallback });
}
function getActiveTheme() {
	return active;
}
//#endregion
export { clearThemeOverrides, getActiveTheme, initTheme, setTheme, setThemeOverrides };
